/* eslint-disable no-unused-vars */
import { Fragment, useRef, useState } from "react";
import useClickOutside from "../../../Hooks/useClickOutside";
import { NavLink, useNavigate } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import { authAtom } from "../../../store/auth";
import { transformAuthUser } from "../../../utils/flatten-attributes";
import { classNames } from "../../../utils";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import FormatQuoteOutlinedIcon from "@mui/icons-material/FormatQuoteOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import StoreMallDirectoryOutlinedIcon from "@mui/icons-material/StoreMallDirectoryOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";

import { useUnreadNotificationCount } from "../../../Hooks/useNotifications";
import {
	MobileNotificationLink,
	UnreadBadge,
} from "../../Notifications/NotificationBell";
import { releaseNotificationsOnSignOut } from "../../../utils/signOut";
import { useMyAccess } from "../../../Hooks/usePermissions";
import { canOpenPath } from "../../../utils/pageAccess";

import TajiriAdmin from "../../../assets/images/TajiriAdmin.svg";
import TajiriAdminWhite from "../../../assets/images/TajiriAdminWhite.svg";
import AppSwitcher from "../../AppSwitcher";

const navGroups = [
	{
		groupId: "main",
		label: null,
		items: [{ title: "Dashboard", icon: DashboardOutlinedIcon, link: "/" }],
	},
	{
		groupId: "catalogue",
		label: "Catalogue",
		icon: InventoryOutlinedIcon,
		items: [
			{ title: "Products", icon: InventoryOutlinedIcon, link: "/products" },
			{ title: "Categories", icon: CategoryOutlinedIcon, link: "/categories" },
			{ title: "Brands", icon: LabelOutlinedIcon, link: "/brands" },
			{
				title: "Brand Pages",
				icon: StoreMallDirectoryOutlinedIcon,
				link: "/brand-pages",
			},
			{
				title: "New Arrivals",
				icon: FiberNewOutlinedIcon,
				link: "/new-arrivals",
			},
			{ title: "Rates", icon: PercentOutlinedIcon, link: "/rates" },
			{
				title: "Tax Rates",
				icon: ReceiptLongOutlinedIcon,
				link: "/tax-rates",
			},
			{
				title: "Size Charts",
				icon: StraightenOutlinedIcon,
				link: "/size-charts",
			},
		],
	},
	{
		groupId: "commerce",
		label: "Commerce",
		icon: ShoppingCartOutlinedIcon,
		items: [
			{
				title: "Sales Orders",
				icon: ShoppingCartOutlinedIcon,
				link: "/salesorder",
			},
			// { title: "Orders", icon: ReceiptOutlinedIcon, link: "/orders" },
			{
				title: "Pending Orders",
				icon: PendingActionsOutlinedIcon,
				link: "/pending-orders",
			},
			{ title: "Quotes", icon: FormatQuoteOutlinedIcon, link: "/quotes" },
			{
				title: "Zoho Quotes",
				icon: FormatQuoteOutlinedIcon,
				link: "/quotes/zoho",
			},
			{ title: "Invoices", icon: DescriptionOutlinedIcon, link: "/invoices" },
			{
				title: "Returns",
				icon: AssignmentReturnOutlinedIcon,
				link: "/returns",
			},
			{ title: "Sample Requests", icon: ScienceOutlinedIcon, link: "/samples" },
			{ title: "Bargains", icon: HandshakeOutlinedIcon, link: "/bargains" },
		],
	},
	{
		groupId: "sourcing",
		label: "Sourcing",
		icon: LocalShippingOutlinedIcon,
		items: [
			{
				title: "Sourcing Categories",
				icon: CategoryOutlinedIcon,
				link: "/sourcing/categories",
			},
			{
				title: "Sourcing Requests",
				icon: RequestQuoteOutlinedIcon,
				link: "/sourcing/requests",
			},
			{
				title: "Backorders",
				icon: PendingActionsOutlinedIcon,
				link: "/sourcing/backorders",
			},
			{
				title: "Special Sourcing Requests",
				icon: RequestQuoteOutlinedIcon,
				link: "/sourcing/special-requests",
			},
		],
	},
	{
		groupId: "productivity",
		label: "Productivity",
		icon: EventNoteOutlinedIcon,
		items: [{ title: "Tasks", icon: TaskAltOutlinedIcon, link: "/tasks" }],
	},
	{
		groupId: "finance",
		label: "Finance",
		icon: AccountBalanceOutlinedIcon,
		items: [
			{
				title: "Finance Requests",
				icon: AccountBalanceOutlinedIcon,
				link: "/finance-requests",
			},
			{
				title: "Vendors Withdrawals",
				icon: AccountBalanceWalletOutlinedIcon,
				link: "/vendors-withdrawals",
			},
			{
				title: "Sales-partners Withdrawals",
				icon: AccountBalanceWalletOutlinedIcon,
				link: "/sales-withdrawals",
			},
			{ title: "Invoicing", icon: DescriptionOutlinedIcon, link: "/invoicing" },
		],
	},
	{
		groupId: "people",
		label: "People",
		icon: PeopleOutlinedIcon,
		items: [
			{ title: "Vendors", icon: StorefrontOutlinedIcon, link: "/vendors" },
			{
				title: "Sales Partners",
				icon: SupportAgentOutlinedIcon,
				link: "/sales-partners",
			},

			{
				title: "Z Customers",
				icon: PeopleOutlinedIcon,
				link: "/zoho-customers",
			},
		],
	},
	{
		groupId: "system",
		label: "System",
		icon: SettingsOutlinedIcon,
		items: [
			{
				title: "Integration",
				icon: ExtensionOutlinedIcon,
				link: "/integration",
			},
			{ title: "Settings", icon: SettingsOutlinedIcon, link: "/settings" },
			{ title: "SEO Settings", icon: TravelExploreOutlinedIcon, link: "/seo-settings" },
			{
				title: "Notifications Manager",
				icon: NotificationsOutlinedIcon,
				link: "/notificationsmanager",
			},
		],
	},
];

const useVisibleNavGroups = () => {
	const { access } = useMyAccess();
	return navGroups
		.map((group) => ({
			...group,
			items: group.items.filter((item) => canOpenPath(access, item.link)),
		}))
		.filter((group) => group.items.length > 0);
};

const Tooltip = ({ children, content, show }) => {
	if (!show) return <>{children}</>;
	return (
		<div className="relative group/tip">
			{children}
			<div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 z-[9999] opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150">
				<div className="bg-gray-900 text-white text-xs font-medium px-2.5 py-1.5 rounded-md whitespace-nowrap shadow-lg">
					{content}
					<div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
				</div>
			</div>
		</div>
	);
};

const GroupTooltip = ({ group, children }) => {
	return (
		<div className="relative group/gtip">
			{children}
			<div className="pointer-events-none absolute left-full top-0 ml-3 z-[9999] opacity-0 group-hover/gtip:opacity-100 transition-opacity duration-150 min-w-[160px]">
				<div className="bg-gray-900 text-white text-xs rounded-lg shadow-xl overflow-hidden">
					<div className="px-3 py-2 font-semibold text-white/60 uppercase tracking-widest text-[10px] border-b border-white/10">
						{group.label}
					</div>
					{group.items.map((item) => (
						<NavLink
							key={item.link}
							to={item.link}
							end
							className={({ isActive }) =>
								classNames(
									"flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors",
									isActive ? "text-white" : "text-white/70"
								)
							}
						>
							<item.icon sx={{ fontSize: 14 }} />
							{item.title}
						</NavLink>
					))}
				</div>
				<div className="absolute right-full top-3 border-4 border-transparent border-r-gray-900" />
			</div>
		</div>
	);
};

const Sidebar = ({ isCollapsed, onToggleCollapse }) => {
	const [openGroups, setOpenGroups] = useState(
		navGroups.reduce((acc, g) => ({ ...acc, [g.groupId]: true }), {})
	);
	const visibleNavGroups = useVisibleNavGroups();
	const [profileBox, setProfileBox] = useState(false);
	const profileRef = useRef(null);
	useClickOutside(profileRef, () => setProfileBox(false));
	const setAuth = useSetAtom(authAtom);
	const auth = useAtomValue(authAtom);
	const flattenedUser = transformAuthUser(auth?.user);
	const navigate = useNavigate();
	const { data: unreadNotifications = 0 } = useUnreadNotificationCount();

	const toggleGroup = (id) =>
		setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

	return (
		<div
			className={classNames(
				"hidden lg:flex flex-col fixed top-0 left-0 h-screen bg-white border-r border-gray-100 shadow-sm z-[100] transition-all duration-300 ease-in-out",
				isCollapsed ? "w-[72px]" : "w-[260px]"
			)}
		>
			<div
				className={classNames(
					"flex items-center h-[64px] flex-shrink-0 border-b border-gray-100 px-4",
					isCollapsed ? "justify-center" : "justify-between"
				)}
			>
				{!isCollapsed && (
					<img
						src={TajiriAdmin}
						alt="Tajiri Admin"
						className="h-8 object-contain"
					/>
				)}
				<button
					onClick={onToggleCollapse}
					className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-[#1b3b5f]"
					title={isCollapsed ? "Expand" : "Collapse"}
				>
					{isCollapsed ? (
						<ChevronRightIcon fontSize="small" />
					) : (
						<MenuOpenIcon fontSize="small" />
					)}
				</button>
			</div>

			<nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 scrollbar-thin">
				{visibleNavGroups.map((group) => (
					<div key={group.groupId} className="mb-1">
						{group.groupId === "main" ? (
							group.items.map((item) => (
								<Tooltip
									key={item.link}
									content={item.title}
									show={isCollapsed}
								>
									<NavLink
										to={item.link}
										end
										className={({ isActive }) =>
											classNames(
												"flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm font-medium group",
												isCollapsed ? "justify-center px-0 mx-2" : "",
												isActive
													? "bg-[#1b3b5f] text-white"
													: "text-[#1b3b5f] hover:bg-gray-50 hover:text-[#1b3b5f]"
											)
										}
									>
										{({ isActive }) => (
											<>
												<item.icon
													sx={{ fontSize: 20 }}
													className={
														isActive
															? "text-white"
															: "text-gray-500 group-hover:text-[#1b3b5f]"
													}
												/>
												{!isCollapsed && <span>{item.title}</span>}
											</>
										)}
									</NavLink>
								</Tooltip>
							))
						) : isCollapsed ? (
							<GroupTooltip group={group}>
								<div className="flex items-center justify-center mx-2 py-2.5 rounded-lg cursor-pointer text-gray-500 hover:bg-gray-50 hover:text-[#1b3b5f] transition-colors">
									<group.icon sx={{ fontSize: 20 }} />
								</div>
							</GroupTooltip>
						) : (
							<>
								<button
									onClick={() => toggleGroup(group.groupId)}
									className="w-full flex items-center justify-between px-5 py-2 text-[12px] font-bold uppercase tracking-widest text-[#1b3b5f] hover:text-[#1b3b5f] transition-colors"
								>
									<span>{group.label}</span>
									{openGroups[group.groupId] ? (
										<KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
									) : (
										<KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
									)}
								</button>

								{openGroups[group.groupId] && (
									<div>
										{group.items.map((item) => (
											<NavLink
												key={item.link}
												to={item.link}
												end
												className={({ isActive }) =>
													classNames(
														"flex items-center gap-3 mx-2 px-3 py-2 rounded-lg transition-all duration-150 text-sm group",
														isActive
															? "bg-[#EEF4FB] text-[#1b3b5f] font-semibold border-l-2 border-[#1b3b5f]"
															: "text-gray-500 hover:bg-gray-50 hover:text-[#1b3b5f]"
													)
												}
											>
												{({ isActive }) => (
													<>
														<item.icon
															sx={{ fontSize: 18 }}
															className={
																isActive
																	? "text-[#1b3b5f]"
																	: "text-gray-400 group-hover:text-[#1b3b5f]"
															}
														/>
														<span>{item.title}</span>
													</>
												)}
											</NavLink>
										))}
									</div>
								)}
							</>
						)}
					</div>
				))}
			</nav>

			<div className="flex-shrink-0 border-t border-gray-100">
				<div
					className={classNames(
						"px-2 py-2 flex gap-1",
						isCollapsed ? "flex-col items-center" : ""
					)}
				>
					<Tooltip content="Notifications" show={isCollapsed}>
						<NavLink
							to="/notifications"
							className={({ isActive }) =>
								classNames(
									"relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
									isActive
										? "bg-[#1b3b5f]/10 text-[#1b3b5f] font-semibold"
										: "text-gray-500 hover:bg-gray-50 hover:text-[#1b3b5f]",
									isCollapsed ? "justify-center px-0 w-10 h-10" : "flex-1"
								)
							}
						>
							<NotificationsOutlinedIcon sx={{ fontSize: 20 }} />
							{!isCollapsed && <span className="flex-1">Notifications</span>}
							<UnreadBadge
								count={unreadNotifications}
								className={isCollapsed ? "absolute -top-0.5 -right-0.5" : ""}
							/>
						</NavLink>
					</Tooltip>
				</div>

				<div className="relative px-2 pb-3" ref={profileRef}>
					{profileBox && !isCollapsed && (
						<div className="absolute bottom-full left-2 right-2 mb-1 bg-white border border-gray-100 rounded-xl shadow-xl py-2 text-sm z-10">
							<div className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 mb-1">
								{flattenedUser?.attributes?.profile_image ||
								flattenedUser?.attributes?.image ? (
									<img
										src={
											flattenedUser?.attributes?.profile_image ||
											flattenedUser?.attributes?.image
										}
										alt=""
										className="w-9 h-9 rounded-full object-cover"
									/>
								) : (
									<div className="w-9 h-9 rounded-full bg-[#1b3b5f]/10 flex items-center justify-center text-[#1b3b5f] font-semibold text-sm">
										{flattenedUser?.attributes?.first_name?.[0]}
									</div>
								)}
								<div className="min-w-0">
									<p className="font-semibold text-gray-800 truncate text-sm">
										{flattenedUser?.attributes?.first_name}{" "}
										{flattenedUser?.attributes?.last_name}
									</p>
									<p className="text-xs text-gray-400 truncate">
										{flattenedUser?.attributes?.email}
									</p>
								</div>
							</div>
							<NavLink
								to="/settings"
								onClick={() => setProfileBox(false)}
								className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-gray-700 rounded-lg mx-1 transition-colors"
							>
								<AccountCircleOutlinedIcon
									sx={{ fontSize: 18 }}
									className="text-gray-400"
								/>
								My Profile
							</NavLink>
							<NavLink
								to="/settings"
								onClick={() => setProfileBox(false)}
								className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-gray-700 rounded-lg mx-1 transition-colors"
							>
								<SettingsOutlinedIcon
									sx={{ fontSize: 18 }}
									className="text-gray-400"
								/>
								Settings
							</NavLink>
							<AppSwitcher onSwitch={() => setProfileBox(false)} />
							<div className="border-t border-gray-100 mt-1 pt-1 mx-1">
								<button
									onClick={async () => {
										setProfileBox(false);
										await releaseNotificationsOnSignOut();
										setAuth(null);
										navigate("/login");
									}}
									className="w-full flex items-center gap-3 px-4 py-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
								>
									<LogoutIcon sx={{ fontSize: 18 }} />
									Sign out
								</button>
							</div>
						</div>
					)}

					<button
						onClick={() => setProfileBox((p) => !p)}
						className={classNames(
							"w-full flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-gray-50 transition-colors",
							isCollapsed ? "justify-center px-0" : ""
						)}
					>
						{flattenedUser?.attributes?.profile_image ||
						flattenedUser?.attributes?.image ? (
							<img
								src={
									flattenedUser?.attributes?.profile_image ||
									flattenedUser?.attributes?.image
								}
								alt=""
								className="w-8 h-8 rounded-full object-cover flex-shrink-0"
							/>
						) : (
							<div className="w-8 h-8 rounded-full bg-[#1b3b5f]/10 flex items-center justify-center text-[#1b3b5f] font-semibold text-xs flex-shrink-0">
								{flattenedUser?.attributes?.first_name?.[0]}
							</div>
						)}
						{!isCollapsed && (
							<>
								<div className="flex-1 text-left min-w-0">
									<p className="text-sm font-medium text-gray-800 truncate">
										{flattenedUser?.attributes?.first_name}{" "}
										{flattenedUser?.attributes?.last_name}
									</p>
									<p className="text-xs text-gray-400 truncate">
										{flattenedUser?.attributes?.email}
									</p>
								</div>
								{profileBox ? (
									<KeyboardArrowUpIcon
										sx={{ fontSize: 18 }}
										className="text-gray-400 flex-shrink-0"
									/>
								) : (
									<KeyboardArrowDownIcon
										sx={{ fontSize: 18 }}
										className="text-gray-400 flex-shrink-0"
									/>
								)}
							</>
						)}
					</button>
				</div>
			</div>
		</div>
	);
};

export const MobileSidebar = () => {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [profileBox, setProfileBox] = useState(false);
	const profileRef = useRef(null);
	useClickOutside(profileRef, () => setProfileBox(false));
	const [openGroups, setOpenGroups] = useState(
		navGroups.reduce((acc, g) => ({ ...acc, [g.groupId]: true }), {})
	);
	const visibleNavGroups = useVisibleNavGroups();
	const setAuth = useSetAtom(authAtom);
	const auth = useAtomValue(authAtom);
	const flattenedUser = transformAuthUser(auth?.user);
	const { data: unreadNotifications = 0 } = useUnreadNotificationCount();

	const toggleGroup = (id) =>
		setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

	const close = () => {
		setOpen(false);
		setProfileBox(false);
	};

	return (
		<>
			<div className="lg:hidden fixed top-0 left-0 w-full h-[60px] bg-[#1b3b5f] text-white z-[9998] flex items-center px-4 justify-between shadow-md">
				<button onClick={() => setOpen(true)} className="p-1">
					<MenuIcon />
				</button>
				<img src={TajiriAdminWhite} alt="logo" className="h-8 object-contain" />
				<MobileNotificationLink />
			</div>
			<div className="lg:hidden h-[60px]" />

			{open && (
				<div
					className="lg:hidden fixed inset-0 bg-black/40 z-[9999] backdrop-blur-sm"
					onClick={close}
				>
					<div
						className="w-[280px] h-full bg-[#1b3b5f] flex flex-col shadow-2xl"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex items-center justify-between px-4 h-[60px] border-b border-white/10 flex-shrink-0">
							<img
								src={TajiriAdminWhite}
								alt=""
								className="h-8 object-contain"
							/>
							<button
								onClick={close}
								className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
							>
								<MenuOpenIcon />
							</button>
						</div>

						<nav className="flex-1 overflow-y-auto py-2 px-2">
							{visibleNavGroups.map((group) => (
								<div key={group.groupId} className="mb-1">
									{group.groupId === "main" ? (
										group.items.map((item) => (
											<NavLink
												key={item.link}
												to={item.link}
												end
												onClick={close}
												className={({ isActive }) =>
													classNames(
														"flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
														isActive
															? "bg-white text-[#1b3b5f]"
															: "text-white/80 hover:bg-white/10 hover:text-white"
													)
												}
											>
												{({ isActive }) => (
													<>
														<item.icon
															sx={{ fontSize: 20 }}
															className={isActive ? "text-[#1b3b5f]" : ""}
														/>
														{item.title}
													</>
												)}
											</NavLink>
										))
									) : (
										<>
											<button
												onClick={() => toggleGroup(group.groupId)}
												className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors"
											>
												<span>{group.label}</span>
												{openGroups[group.groupId] ? (
													<KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
												) : (
													<KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
												)}
											</button>
											{openGroups[group.groupId] && (
												<div>
													{group.items.map((item) => (
														<NavLink
															key={item.link}
															to={item.link}
															end
															onClick={close}
															className={({ isActive }) =>
																classNames(
																	"flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
																	isActive
																		? "bg-white/15 text-white font-semibold border-l-2 border-white"
																		: "text-white/70 hover:bg-white/10 hover:text-white"
																)
															}
														>
															{({ isActive }) => (
																<>
																	<item.icon
																		sx={{ fontSize: 18 }}
																		className={
																			isActive ? "text-white" : "text-white/50"
																		}
																	/>
																	{item.title}
																</>
															)}
														</NavLink>
													))}
												</div>
											)}
										</>
									)}
								</div>
							))}
						</nav>

						<div className="flex-shrink-0 border-t border-white/10">
							<div className="px-2 py-2">
								<NavLink
									to="/notifications"
									onClick={close}
									className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors"
								>
									<NotificationsOutlinedIcon sx={{ fontSize: 20 }} />
									<span className="flex-1">Notifications</span>
									<UnreadBadge count={unreadNotifications} />
								</NavLink>
								<NavLink
									to="/settings"
									onClick={close}
									className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors"
								>
									<SettingsOutlinedIcon sx={{ fontSize: 20 }} />
									Settings
								</NavLink>
							</div>

							<div className="relative px-2 pb-3" ref={profileRef}>
								{profileBox && (
									<div className="absolute bottom-full left-0 right-0 mx-2 mb-1 bg-white rounded-xl shadow-xl py-2 text-sm z-10">
										<div className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 mb-1">
											{flattenedUser?.attributes?.profile_image ||
											flattenedUser?.attributes?.image ? (
												<img
													src={
														flattenedUser?.attributes?.profile_image ||
														flattenedUser?.attributes?.image
													}
													alt=""
													className="w-9 h-9 rounded-full object-cover"
												/>
											) : (
												<div className="w-9 h-9 rounded-full bg-[#1b3b5f]/10 flex items-center justify-center text-[#1b3b5f] font-semibold text-sm">
													{flattenedUser?.attributes?.first_name?.[0]}
												</div>
											)}
											<div className="min-w-0">
												<p className="font-semibold text-gray-800 text-sm truncate">
													{flattenedUser?.attributes?.first_name}{" "}
													{flattenedUser?.attributes?.last_name}
												</p>
												<p className="text-xs text-gray-400 truncate">
													{flattenedUser?.attributes?.email}
												</p>
											</div>
										</div>
										<NavLink
											to="/settings"
											onClick={close}
											className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-gray-700 transition-colors"
										>
											<AccountCircleOutlinedIcon
												sx={{ fontSize: 18 }}
												className="text-gray-400"
											/>
											My Profile
										</NavLink>
										<NavLink
											to="/settings"
											onClick={close}
											className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-gray-700 transition-colors"
										>
											<SettingsOutlinedIcon
												sx={{ fontSize: 18 }}
												className="text-gray-400"
											/>
											Settings
										</NavLink>
										<AppSwitcher onSwitch={() => close()} />
										<div className="border-t border-gray-100 mt-1 pt-1">
											<button
												onClick={async () => {
													close();
													await releaseNotificationsOnSignOut();
													setAuth(null);
													navigate("/login");
												}}
												className="w-full flex items-center gap-3 px-4 py-2 hover:bg-red-50 text-red-500 transition-colors"
											>
												<LogoutIcon sx={{ fontSize: 18 }} />
												Sign out
											</button>
										</div>
									</div>
								)}

								<button
									onClick={() => setProfileBox((p) => !p)}
									className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 transition-colors"
								>
									{flattenedUser?.attributes?.profile_image ||
									flattenedUser?.attributes?.image ? (
										<img
											src={
												flattenedUser?.attributes?.profile_image ||
												flattenedUser?.attributes?.image
											}
											alt=""
											className="w-8 h-8 rounded-full object-cover flex-shrink-0"
										/>
									) : (
										<div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
											{flattenedUser?.attributes?.first_name?.[0]}
										</div>
									)}
									<div className="flex-1 text-left min-w-0">
										<p className="text-sm font-medium text-white truncate">
											{flattenedUser?.attributes?.first_name}{" "}
											{flattenedUser?.attributes?.last_name}
										</p>
										<p className="text-xs text-white/50 truncate">
											{flattenedUser?.attributes?.email}
										</p>
									</div>
									{profileBox ? (
										<KeyboardArrowUpIcon
											sx={{ fontSize: 18 }}
											className="text-white/60 flex-shrink-0"
										/>
									) : (
										<KeyboardArrowDownIcon
											sx={{ fontSize: 18 }}
											className="text-white/60 flex-shrink-0"
										/>
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default Sidebar;
